<template>
    <div class="full-background">
        <div class="container">
            <div class="box">
                <div class="form-navbar is-flex">
                    <span class="nav-button is-invisible">←</span>
                    <div class="abacus">
                        <div class="abacus-bead"><p>1</p></div>
                        <div class="abacus-space" :class="{'full': currentStep === 1}"></div>
                        <div class="abacus-bead"><p>2</p></div>
                        <div class="abacus-space" :class="{'full': currentStep === 2}"></div>
                        <div class="abacus-bead"><p>3</p></div>
                    </div>
                    <a class="nav-button" type="button" @click="goBack()">⨯</a>
                </div>
                <br>
                <form autocomplete="on">

                    <div class="form-step" v-if="currentStep === 1">
                        <div>
                            <p class="has-text-centered"><strong>Where do you live?</strong></p>
                            <br>
                        </div>
                        <p class="has-text-centered" id="small-reminder" v-if="addressTouched">*If you live in a flat, just enter your street address without the unit number</p>
                        <div class="field">
                            <div class="control">
                                <GmapInput
                                    class="input is-rounded"
                                    :componentRestrictions="{country: 'ZA'}"
                                    @place_changed="setPlace"
                                    @input="addressTouched = true"
                                    :value="form.address_input"
                                    ref="mapInput"
                                    placeholder="Enter your home address"
                                ></GmapInput>
                            </div>
                        </div>
                    </div>

                    <div class="form-step" v-if="currentStep === 2">
                        <p class="has-text-centered"><strong>How should we contact you?</strong></p>
                        <br>
                        <Field
                            autocomplete="given-name"
                            placeholderText="First name"
                            id="fname"
                            name="fname"
                            v-model="form.fname"
                            :inputDirty="$v.form.fname.$dirty"
                            :inputRequired="$v.form.fname.required"
                            @input="$v.form.fname.$touch()"
                            type="text"
                        >
                        </Field>
                        <Field
                            autocomplete="family-name"
                            placeholderText="Surname"
                            id="lname"
                            name="lname"
                            v-model="form.sname"
                            :inputDirty="$v.form.sname.$dirty"
                            :inputRequired="$v.form.sname.required"
                            @input="$v.form.sname.$touch()"
                            type="text"
                        >
                        </Field>
                        <Field
                            autocomplete="mobile tel-national"
                            placeholderText="Cell number"
                            id="mobile"
                            name="mobile"
                            v-model="form.cell_num"
                            :inputDirty="$v.form.cell_num.$dirty"
                            :inputExtra="$v.form.cell_num.checkCell"
                            :inputRequired="$v.form.cell_num.required"
                            @input="$v.form.cell_num.$touch()"
                            type="tel"
                            v-mask="'0## ### ####'"
                        >
                            <p class="help is-danger" v-if="$v.form.cell_num.$dirty & $v.form.cell_num.required & !$v.form.cell_num.checkCell">Please enter a valid cell number (e.g. 084 123 4567)</p>
                        </Field>
                    </div>

                    <div class="form-step" v-if="currentStep === 3">
                        <p class="has-text-centered"><strong>Would you like a preliminary valuation?</strong></p>
                        <div class="field has-text-centered">
                            <div class="control radio-control">
                                <label class="radio">
                                    <input name="prelim_valuation" type="radio" value="Yes" v-model="form.prelim_val">
                                    <strong>Yes</strong>
                                </label>
                                <label class="radio">
                                    <input name="prelim_valuation" type="radio" value="No" v-model="form.prelim_val" @change="$v.form.$touch()">
                                    <strong>No</strong>
                                </label>
                            </div>
                        </div>
                        <br>
                        <div v-if="form.prelim_val === 'Yes'">
                            <Field
                                placeholderText="ID Number"
                                id="id_number"
                                name="id_number"
                                v-model="form.id_number"
                                :inputDirty="$v.form.id_number.$dirty"
                                :inputExtra="$v.form.id_number.checkIdNumber"
                                :inputRequired="$v.form.id_number.required"
                                @input="$v.form.id_number.$touch()"
                                type="number"
                                v-mask="'#############'"
                            >
                                <p class="help is-danger" v-if="$v.form.id_number.$dirty & $v.form.id_number.required & !$v.form.id_number.checkIdNumber">Please enter a valid ID number</p>
                            </Field>
                            <Field
                                autocomplete="home email"
                                placeholderText="Email address"
                                id="email"
                                name="email"
                                v-model="form.email_addr"
                                :inputDirty="$v.form.email_addr.$dirty"
                                :inputExtra="$v.form.email_addr.checkCell"
                                :inputRequired="$v.form.email_addr.required"
                                @input="$v.form.email_addr.$touch()"
                                type="email"
                            >
                                <p class="help is-danger" v-if="$v.form.email_addr.$dirty & $v.form.email_addr.required & !$v.form.email_addr.email">Please enter a valid email address</p>
                            </Field>
                            <Field
                                autocomplete="new-password"
                                placeholderText="Confirm email address"
                                id="email_addr_confirm"
                                v-model="form.email_addr_confirm"
                                :inputDirty="$v.form.email_addr_confirm.$dirty"
                                :inputExtra="$v.form.email_addr_confirm.sameAs"
                                :inputRequired="$v.form.email_addr_confirm.required"
                                @input="$v.form.email_addr_confirm.$touch()"
                                type="email"
                            >
                                <p class="help is-danger" v-if="$v.form.email_addr_confirm.$dirty & $v.form.email_addr_confirm.required & !$v.form.email_addr_confirm.sameAsEmail">Please make sure both email addresses match</p>
                            </Field>
                            <br>
                            <p class="has-text-centered">Fill in your details above and we will email you your preliminary property valuation report and call you to discuss it</p>
                            <br>
                            <p class="is-size-7 has-text-centered">*We use your ID to identify your property before we call you. This way, we can provide you with the relevant information based on your area as well as an upfront preliminary valuation of your property</p>
                        </div>
                    </div>

                    <div class="form-step small has-text-centered" v-if="currentStep === 4">
                        <p class="title">Thank you!</p>
                        <p><strong>A NestEgg Consultant will be in contact with you shortly.</strong></p>
                        <br>
                        <a class="button is-rounded is-primary" v-if="currentStep === 4" @click="goBack()">Okay</a>
                    </div>

                    <br>
                    <div class="nav-button-container">
                        <button 
                            class="button is-rounded is-primary" 
                            :disabled="!((currentStep === 1 && currentPlace !== null) || $v.form.$anyDirty) || $v.form.$invalid"
                            type="button" 
                            v-if="currentStep < 3"
                            v-on:click="currentStep++"
                        >Next</button>
                        <button 
                            class="button is-rounded is-primary" 
                            :disabled="!$v.form.$anyDirty || $v.form.$invalid"
                            type="button" 
                            v-if="currentStep === 3"
                            v-on:click="submitForm"
                        >{{ sendButtonTxt }}</button>
                        <button 
                            class="button is-rounded is-primary" 
                            type="button" 
                            v-if="currentStep > 1 && currentStep < 4"
                            v-on:click="prevStep"
                        >Prev</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
    import axios from 'axios'
    import Field from '../components/Field.vue'
    import GmapInput from 'vue2-google-maps/src/components/autocomplete.vue'
    import { required, email, sameAs } from 'vuelidate/lib/validators/'
    import { checkCell } from '../modules/cell_number.js'
    import { checkIdNumber } from '../modules/id_number.js'

    export default {
        components: {
            Field,
            GmapInput
        },
        computed: {
            rules() {
                var currentRules = {};
                switch (this.currentStep) {
                    case 1:
                        currentRules = {
                            address_input: { 
                                required 
                            }
                        };
                        break;
                    case 2:
                        currentRules = {
                            fname: {
                                required
                            },
                            sname: {
                                required
                            },
                            cell_num: {
                                required,
                                checkCell
                            }
                        };
                        break;
                    case 3:
                        if (this.form.prelim_val === "Yes") {
                            currentRules = {
                                id_number: {
                                    required,
                                    checkIdNumber
                                },
                                email_addr: {
                                    required,
                                    email
                                },
                                email_addr_confirm: {
                                    required,
                                    sameAsEmail: sameAs('email_addr')
                                }
                            };
                        }
                }
                return currentRules;
            }
        },
        beforeRouteEnter (to, from, next) {
            next(vm => {
                vm.$parent.navVis = false;
            })
        },
        beforeRouteLeave (to, from, next) {
            this.$parent.navVis = true;
            next()
        },
        created() {
            if (this.$parent.address !== null) {
                this.currentStep = 2;
                this.setPlace(this.$parent.address, false);
            }
        },
        data() {
            return {
                form: {
                    address_input: '',
                    fname: '',
                    sname: '',
                    cell_num: '',
                    email_addr: '',
                    email_addr_confirm: '',
                    id_number: '',
                    prelim_val: ''
                },
                addressTouched: false,
                currentStep: 1,
                currentPlace: null,
                sendButtonTxt: "Call me back"
            }
        },
        methods: {
            goBack() {
                this.$router.go(-1);
            },
            prevStep() {
                this.currentStep--;
                this.$v.form.$touch();
            },
            setPlace(place, updateParent = true) {
                this.currentPlace = place;
                this.form.address_input = place.formatted_address;

                if (updateParent) {
                    this.$v.form.address_input.$touch();
                    this.$parent.address = place;
                }
            },
            submitForm() {
                var orig_this = this,
                    address_components = orig_this.$parent.address.address_components,
                    clean_address = {},
                    oldSendButtonTxt = this.sendButtonTxt;
                this.sendButtonTxt = 'Sending...';
                for (var i = 0; i < address_components.length; i++) {
                    clean_address[address_components[i].types[0]] = address_components[i].long_name;
                }

                axios.post(process.env.VUE_APP_POST_URL + '/api/submit.php', 
                    JSON.stringify(
                        {
                            'LeadId': orig_this.$parent.leadid,
                            'FirstName': orig_this.form.fname,
                            'Surname': orig_this.form.sname,
                            'CellNumber': orig_this.form.cell_num,
                            'EmailAddress': orig_this.form.email_addr,
                            'IdNumber': orig_this.form.id_number,
                            'StreetNumber': clean_address.street_number,
                            'UnitNumberComplexStreet': clean_address.route,
                            'Suburb': typeof(clean_address.sublocality_level_1) !== typeof(undefined) ? clean_address.sublocality_level_1 : clean_address.sublocality_level_2,
                            'PostalCode': clean_address.postal_code,
                            'CityTown': clean_address.locality,
                            'Province': clean_address.administrative_area_level_1,
                            'SubmissionType': 'ContactMe',
                            'PrelimValuation': orig_this.form.prelim_val,
                            'Token': document.getElementById('csrf').value
                        }
                    )
                )
                .finally(function() {
                    orig_this.currentStep++;
                    orig_this.sendButtonTxt = oldSendButtonTxt;
                });
            }
        },
        validations() {
            return {
                form: this.rules 
            }
        }
    }
</script>

<style lang="scss">
    @import '../assets/sass/colours.scss';

    .pac-container {
        z-index: 10000 !important;
    }

    .full-background {
        background: linear-gradient(135deg, #B2DFDB 0, #B5E4FA 100%);
        width: 100vw;
        min-height: 100vh;
        padding: 3em 0;
    }

    .form-navbar {
        align-items: flex-start;

        .nav-button {
            background: none;
            color: $black !important;
            font-size: 2em;
            position: relative;
            top: -1rem;
        }

        .nav-button:hover {
            border: none;
        }
    }

    .abacus {
        display: flex;
        flex-flow: row nowrap;
        flex: 1 1 auto;
        margin: 1.5rem 20%;
        background: linear-gradient(0deg, white 47%, black 50%, white 53%);

        $abacus-bead-size: 2em;
        .abacus-bead {
            background-color: $blue;
            border-radius: 100%;
            height: $abacus-bead-size;
            width: $abacus-bead-size;
            padding-top: 0.25em;

            color: white;
            font-weight: bold;
            text-align: center;
        }

        .abacus-space {
            transition: flex-grow .7s ease;
        }
        .abacus-space.full {
            flex-grow: 1;
        }
    }

    form {
        padding: 0 10%;
        margin: 0 auto;
        max-width: 900px;
    }

    .radio-control {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    $radio-size: 2em;
    $radio-gap: 0.75em;
    $radio-margin: 1em;
    input[type=radio] {
        appearance: none;
        border: solid 2px $grey-lighter;
        border-radius: 100%;
        width: $radio-size;
        height: $radio-size;
        vertical-align: middle;
        margin: $radio-margin;
        transition: all .2s ease;
    }

    input[type=radio]::before {
        position: absolute;
        border-radius: 100%;
        height: $radio-size - $radio-gap;
        width: $radio-size - $radio-gap;
        left: $radio-margin + ($radio-gap / 2);
        top: $radio-margin + ($radio-gap / 2);
        content: '';
        background-color: $primary;
        transform: scale(0);
        transition: all .2s ease;
    }

    input[type=radio]:checked {
        border-color: $primary;
    }

    input[type=radio]:checked::before {
        transform: scale(1, 1);
        transform-origin: center;
    }

    input[type=number]::-webkit-inner-spin-button,
    input[type=number]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    input[type=number] {
        -moz-appearance: textfield;
    }

    .form-step {
        min-height: 50vh;
        transition: all .2s ease;
    }

    .form-step.small {
        min-height: 0;
    }

    .nav-button-container {
        display: flex;
        flex-direction: row-reverse;
        justify-content: space-between;
    }
</style>